import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sanitizeStrict } from '../../lib/sanitize';
import { saveDraft, loadDraft, clearDraft, formatSavedAt } from '../../lib/drafts';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Sparkles, Plus, Printer, Trash2, FileText, Loader2, Mail, Wand2, Package, Phone, Share2, Link as LinkIcon, MessageCircle, Pencil, Save, X, Receipt, CheckCircle2, CreditCard, Building2, Copy, Eye, AlertCircle, CalendarClock, Send } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { SendEmailDialog } from '../../components/SendEmailDialog';
import { DescrizioneAIDialog, SuggerimentiDialog, FollowUpDialog } from '../../components/AIPanels';
import { OverdueBadge, daysOverdue } from '../../components/OverdueBadge';

const CATEGORIE = ['Idraulica', 'Elettricità', 'Muratura', 'Tinteggiatura', 'Piastrelle', 'Ristrutturazione bagno', 'Ristrutturazione cucina', 'Ristrutturazione totale', 'Coperture', 'Cartongesso', 'Falegnameria', 'Vetraio', 'Altro'];

const LS_KEY = 'cantiereai.prev_defaults_v1';

function loadDefaults() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (raw) return JSON.parse(raw);
    } catch (_) {}
    return { categoria: 'Idraulica', iva: 22, last_cliente: '' };
}
function saveDefaults(patch) {
    try {
        const cur = loadDefaults();
        localStorage.setItem(LS_KEY, JSON.stringify({ ...cur, ...patch }));
    } catch (_) {}
}

function normalizePhone(raw) {
    if (!raw) return '';
    const trimmed = raw.replace(/\s|\-|\(|\)/g, '');
    if (trimmed.startsWith('+')) return trimmed.replace(/^\+/, '');
    if (trimmed.startsWith('00')) return trimmed.replace(/^00/, '');
    // assume Italian if 9-10 digits starting with 3
    if (/^3\d{8,9}$/.test(trimmed)) return '39' + trimmed;
    return trimmed;
}

export default function Preventivi() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const defaults = loadDefaults();
    const [items, setItems] = useState([]);
    const [openForm, setOpenForm] = useState(false);
    const [openDetail, setOpenDetail] = useState(null);
    const [emailDialog, setEmailDialog] = useState(null);
    const [aiDescDialog, setAiDescDialog] = useState(false);
    const [suggDialog, setSuggDialog] = useState(false);
    const [followUp, setFollowUp] = useState(null);
    const [loading, setLoading] = useState(false);
    const [quickMode, setQuickMode] = useState(true);
    const [phoneEdit, setPhoneEdit] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);
    const [form, setForm] = useState({
        categoria: defaults.categoria || 'Idraulica',
        descrizione_lavoro: '',
        cliente: '',
        cliente_telefono: '',
        metratura: '',
        indirizzo: '',
        iva: defaults.iva || 22,
        note_aggiuntive: '',
    });

    // --- Draft auto-save (P0 reliability feature) ---
    const DRAFT_SLOT = 'preventivo';
    const [draftHint, setDraftHint] = useState(null); // { savedAt } when a saved draft exists
    const formInitialized = React.useRef(false);

    // Auto-save form to localStorage as user types (only meaningful fields trigger save)
    useEffect(() => {
        if (!openForm) return; // don't autosave when modal is closed (already handled on close)
        if (!formInitialized.current) {
            // first render after opening — skip autosave once to avoid clobbering loaded data
            formInitialized.current = true;
            return;
        }
        const id = window.setTimeout(() => {
            saveDraft(DRAFT_SLOT, { ...form, quickMode }, ['cliente', 'descrizione_lavoro', 'cliente_telefono']);
        }, 500); // small debounce
        return () => window.clearTimeout(id);
    }, [form, quickMode, openForm]);

    // When opening the modal, surface any existing draft via banner
    useEffect(() => {
        if (openForm) {
            const draft = loadDraft(DRAFT_SLOT);
            if (draft && draft.data) {
                setDraftHint({ savedAt: draft.savedAt });
            } else {
                setDraftHint(null);
            }
            formInitialized.current = false;
        } else {
            // Modal closed — if the user closed without submit, draft remains in storage.
            // We just toast a small reminder if the form had meaningful data.
            const draft = loadDraft(DRAFT_SLOT);
            if (draft) {
                // Soft, non-intrusive notice. No-op if already shown recently.
            }
            setDraftHint(null);
        }
    }, [openForm]);

    const resumeDraft = () => {
        const draft = loadDraft(DRAFT_SLOT);
        if (!draft || !draft.data) {
            setDraftHint(null);
            return;
        }
        const d = draft.data;
        setForm({
            categoria: d.categoria || 'Idraulica',
            descrizione_lavoro: d.descrizione_lavoro || '',
            cliente: d.cliente || '',
            cliente_telefono: d.cliente_telefono || '',
            metratura: d.metratura || '',
            indirizzo: d.indirizzo || '',
            iva: d.iva || 22,
            note_aggiuntive: d.note_aggiuntive || '',
        });
        if (typeof d.quickMode === 'boolean') setQuickMode(d.quickMode);
        setDraftHint(null);
    };

    const discardDraft = () => {
        clearDraft(DRAFT_SLOT);
        setDraftHint(null);
        setForm({
            categoria: defaults.categoria || 'Idraulica',
            descrizione_lavoro: '',
            cliente: '',
            cliente_telefono: '',
            metratura: '',
            indirizzo: '',
            iva: defaults.iva || 22,
            note_aggiuntive: '',
        });
        toast.success(t('pages.draft_discarded'));
    };

    // Custom close handler: persist draft if meaningful, then close
    const handleOpenFormChange = (isOpen) => {
        if (!isOpen) {
            // attempting to close — persist immediately (no debounce) and notify if anything was kept
            const saved = saveDraft(DRAFT_SLOT, { ...form, quickMode }, ['cliente', 'descrizione_lavoro', 'cliente_telefono']);
            if (saved) {
                toast.info(t('pages.draft_close_save_hint'), { duration: 3000 });
            }
        }
        setOpenForm(isOpen);
    };

    const load = useCallback(async () => {
        try {
            const res = await api.get('/preventivi/list');
            setItems(res.data);
        } catch (e) {
            toast.error(t('common.error_load'));
        }
    }, [t]);
    useEffect(() => { load(); }, [load]);

    // sync phoneEdit when opening a detail
    useEffect(() => {
        if (openDetail) {
            setPhoneEdit(openDetail.cliente_telefono || '');
        } else {
            setEditMode(false);
            setEditData(null);
        }
    }, [openDetail]);

    const reuseLast = () => {
        const d = loadDefaults();
        setForm((f) => ({ ...f, categoria: d.categoria || f.categoria, iva: d.iva || f.iva, cliente: d.last_cliente || f.cliente }));
    };

    const generate = async (e) => {
        e.preventDefault();
        if (!form.descrizione_lavoro || !form.cliente) {
            toast.error(t('pages.prev_toast_compila'));
            return;
        }
        setLoading(true);
        try {
            const res = await api.post('/preventivi/generate', {
                ...form,
                metratura: form.metratura ? parseFloat(form.metratura) : null,
            });
            toast.success(t('pages.prev_toast_generated'));
            saveDefaults({ categoria: form.categoria, iva: form.iva, last_cliente: form.cliente });
            clearDraft(DRAFT_SLOT);
            setOpenForm(false);
            setOpenDetail(res.data);
            load();
            setForm({ categoria: form.categoria, descrizione_lavoro: '', cliente: '', cliente_telefono: '', metratura: '', indirizzo: '', iva: form.iva, note_aggiuntive: '' });
        } catch (e) {
            toast.error(e.response?.data?.detail || t('pages.prev_toast_gen_err'));
        } finally { setLoading(false); }
    };

    const remove = async (id) => {
        if (!window.confirm(t('common.confirm_delete'))) return;
        await api.delete(`/preventivi/${id}`);
        toast.success(t('pages.prev_toast_deleted'));
        load();
    };

    const ensureShareToken = async () => {
        if (!openDetail) return null;
        if (openDetail.share_token) return openDetail.share_token;
        try {
            const res = await api.post(`/preventivi/${openDetail.id}/share`);
            const tok = res.data?.share_token;
            setOpenDetail((d) => d ? { ...d, share_token: tok } : d);
            return tok;
        } catch (e) {
            toast.error(t('pages.prev_toast_gen_err'));
            return null;
        }
    };

    const savePhone = async () => {
        if (!openDetail) return;
        const norm = phoneEdit.trim();
        try {
            await api.put(`/preventivi/${openDetail.id}`, { cliente_telefono: norm });
            setOpenDetail((d) => d ? { ...d, cliente_telefono: norm } : d);
            setItems((prev) => prev.map((p) => p.id === openDetail.id ? { ...p, cliente_telefono: norm } : p));
            toast.success(t('common.saved') || 'Salvato');
        } catch (e) {
            toast.error(t('pages.prev_toast_gen_err'));
        }
    };

    // Public share URL: prefer the explicit production domain env so preview
    // sessions still generate cantiereai.com links (what the client will see).
    // Falls back to window.location.origin for local dev when the env is unset.
    const publicUrl = (tok) => {
        const base = process.env.REACT_APP_PUBLIC_BASE_URL || 'https://www.cantiereai.com';
        return `${base.replace(/\/$/, '')}/p/${tok}`;
    };

    // -------- Genera Fattura da Preventivo --------
    const [generatingFattura, setGeneratingFattura] = useState(false);

    // -------- Workflow Accetta + Proforma --------
    const [accettaDialog, setAccettaDialog] = useState(false);
    const [accettaPct, setAccettaPct] = useState(20);
    const [accettaNote, setAccettaNote] = useState('');
    const [accettaLoading, setAccettaLoading] = useState(false);
    const [proforma, setProforma] = useState(null); // loaded proforma linked to openDetail
    const [confermaPaganDialog, setConfermaPagDialog] = useState(false);
    const [pagMetodo, setPagMetodo] = useState('bonifico');
    const [pagRiferimento, setPagRiferimento] = useState('');
    const [pagLoading, setPagLoading] = useState(false);

    // Load proforma when openDetail has a proforma_id
    useEffect(() => {
        const pid = openDetail?.proforma_id;
        if (pid) {
            api.get(`/proforme/${pid}`).then((r) => setProforma(r.data)).catch(() => setProforma(null));
        } else {
            setProforma(null);
        }
    }, [openDetail?.proforma_id, openDetail?.id]);

    const accettaPreventivo = async () => {
        if (!openDetail) return;
        setAccettaLoading(true);
        try {
            const res = await api.post(`/preventivi/${openDetail.id}/accetta`, {
                anticipo_pct: parseFloat(accettaPct) || 20,
                note: accettaNote,
            });
            toast.success(t('pages.prev_accetta_success'));
            setAccettaDialog(false);
            setProforma(res.data);
            // refresh openDetail and list
            const refreshed = await api.get('/preventivi/list');
            setItems(refreshed.data);
            const updated = refreshed.data.find((p) => p.id === openDetail.id);
            if (updated) setOpenDetail(updated);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('pages.prev_toast_gen_err'));
        } finally {
            setAccettaLoading(false);
        }
    };

    const confermaPagamentoProforma = async () => {
        if (!proforma) return;
        setPagLoading(true);
        try {
            await api.post(`/proforme/${proforma.id}/conferma-pagamento`, {
                metodo: pagMetodo,
                riferimento: pagRiferimento,
            });
            toast.success(t('pages.prev_proforma_conferma_ok'));
            setConfermaPagDialog(false);
            setPagRiferimento('');
            // refresh
            const refreshedPf = await api.get(`/proforme/${proforma.id}`);
            setProforma(refreshedPf.data);
            const refreshed = await api.get('/preventivi/list');
            setItems(refreshed.data);
            const updated = refreshed.data.find((p) => p.id === openDetail.id);
            if (updated) setOpenDetail(updated);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('pages.prev_toast_gen_err'));
        } finally {
            setPagLoading(false);
        }
    };
    // -------- /Workflow --------

    // -------- Proforma: payment terms + send-to-client --------
    const PAYMENT_TERM_OPTIONS = [
        { value: 'vista_fattura', labelKey: 'pages.prev_pt_vista_fattura' },
        { value: 'fine_mese', labelKey: 'pages.prev_pt_fine_mese' },
        { value: '30gg_fine_mese', labelKey: 'pages.prev_pt_30gg_fine_mese' },
        { value: '60gg_fine_mese', labelKey: 'pages.prev_pt_60gg_fine_mese' },
    ];
    const formatPaymentTermLabel = (val) => {
        const opt = PAYMENT_TERM_OPTIONS.find((o) => o.value === val);
        return opt ? t(opt.labelKey) : (val || '—');
    };
    const formatDate = (iso) => {
        if (!iso) return '—';
        try { return new Date(String(iso).slice(0, 10)).toLocaleDateString(); } catch { return String(iso).slice(0, 10); }
    };

    const updatePaymentTerms = async (newTerm) => {
        if (!proforma) return;
        try {
            const res = await api.patch(`/proforme/${proforma.id}/payment-terms`, { payment_terms: newTerm });
            setProforma(res.data);
            toast.success(t('pages.prev_pt_saved'));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        }
    };

    const [sendDialog, setSendDialog] = useState(false);
    const copyClientLink = async () => {
        if (!proforma?.share_token) return;
        const base = process.env.REACT_APP_PUBLIC_BASE_URL || 'https://www.cantiereai.com';
        const url = `${base.replace(/\/$/, '')}/p/proforma/${proforma.share_token}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success(t('pages.prev_pt_link_copied'));
        } catch {
            // fallback: open prompt
            window.prompt(t('pages.prev_pt_link_copy_manual'), url);
        }
    };

    // Generic PDF download helper (for preventivo or proforma)
    const downloadDocPdf = async (kind, id, defaultFilename) => {
        const token = localStorage.getItem('cantiere_token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/${kind}/${id}/pdf`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { toast.error(t('common.generic_error')); return; }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const cd = res.headers.get('content-disposition') || '';
        const m = cd.match(/filename="([^"]+)"/);
        a.download = m ? m[1] : defaultFilename;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(t('pages.prev_pdf_downloaded') || 'PDF scaricato');
    };
    // -------- /Proforma extras --------

    const WORKFLOW_LABELS = {
        bozza: t('pages.prev_workflow_bozza'),
        inviata: t('pages.prev_workflow_inviato'),
        inviato: t('pages.prev_workflow_inviato'),
        accettato: t('pages.prev_workflow_accettato'),
        lavori_in_corso: t('pages.prev_workflow_in_corso'),
        completato: t('pages.prev_workflow_completato'),
        rifiutato: t('pages.prev_workflow_rifiutato'),
    };
    const WORKFLOW_COLORS = {
        bozza: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]',
        inviata: 'bg-[hsl(210_90%_94%)] text-[hsl(210_90%_40%)]',
        inviato: 'bg-[hsl(210_90%_94%)] text-[hsl(210_90%_40%)]',
        accettato: 'bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)]',
        lavori_in_corso: 'bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)]',
        completato: 'bg-[hsl(145_55%_34%)] text-white',
        rifiutato: 'bg-[hsl(0_80%_95%)] text-[hsl(0_72%_52%)]',
    };
    const wfBadge = (p) => {
        const wf = p.stato_workflow || p.stato || 'bozza';
        return <Badge className={`${WORKFLOW_COLORS[wf] || WORKFLOW_COLORS.bozza} text-xs`} data-testid={`wf-badge-${p.id}`}>{WORKFLOW_LABELS[wf] || wf}</Badge>;
    };

    const generaFattura = async () => {
        if (!openDetail) return;
        setGeneratingFattura(true);
        try {
            // 1) Get pre-filled data from quote
            const pre = await api.post(`/fatture/da-preventivo/${openDetail.id}`);
            const data = pre.data;
            // 2) Create the invoice with that data
            const fatturaPayload = {
                cliente_nome: data.cliente_nome || openDetail.cliente,
                cliente_indirizzo: data.cliente_indirizzo || openDetail.indirizzo || '',
                cliente_email: openDetail.cliente_email || '',
                righe: data.righe || [],
                causale: data.causale || '',
                note: `Generata da Preventivo ${openDetail.numero}`,
            };
            const created = await api.post('/fatture/create', fatturaPayload);
            toast.success(t('pages.prev_fattura_creata'));
            // Close current dialog and navigate to invoices page, opening the new one
            setOpenDetail(null);
            navigate(`/app/fatture?open=${created.data.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('pages.prev_fattura_err'));
        } finally {
            setGeneratingFattura(false);
        }
    };
    // -------- /Genera Fattura --------


    // -------- Edit mode --------
    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    const recomputeTotals = (data) => {
        const voci = (data.voci || []).map((v) => ({
            ...v,
            quantita: Number(v.quantita) || 0,
            prezzo_unitario: round2(v.prezzo_unitario),
            totale: round2((Number(v.quantita) || 0) * (Number(v.prezzo_unitario) || 0)),
        }));
        const subtotale = round2(voci.reduce((s, v) => s + (v.totale || 0), 0));
        const ivaPerc = Number(data.iva_percentuale) || 0;
        const iva_importo = round2(subtotale * ivaPerc / 100);
        const totale_finale = round2(subtotale + iva_importo);
        return { ...data, voci, subtotale, iva_importo, totale_finale };
    };

    // Ref to always read the latest recomputeTotals inside long-lived listeners
    // (e.g. beforeunload) without re-registering them on every render.
    const recomputeTotalsRef = useRef(recomputeTotals);
    useEffect(() => {
        recomputeTotalsRef.current = recomputeTotals;
    });

    const startEdit = () => {
        if (!openDetail) return;
        // Deep clone contenuto into a flat editable structure
        const c = openDetail.contenuto || {};
        setEditData(JSON.parse(JSON.stringify({
            titolo: c.titolo || '',
            descrizione_lavoro: c.descrizione_lavoro || '',
            voci: (c.voci || []).map((v) => ({
                tipo: v.tipo || 'materiali',
                descrizione: v.descrizione || '',
                quantita: v.quantita || 0,
                unita: v.unita || '',
                prezzo_unitario: v.prezzo_unitario || 0,
                totale: v.totale || 0,
            })),
            subtotale: c.subtotale || 0,
            iva_percentuale: c.iva_percentuale || 22,
            iva_importo: c.iva_importo || 0,
            totale_finale: c.totale_finale || 0,
            tempi_stimati_giorni: c.tempi_stimati_giorni || 0,
            note: c.note || '',
        })));
        setEditMode(true);
    };

    const cancelEdit = () => {
        setEditMode(false);
        setEditData(null);
    };

    const updateVoce = (idx, patch) => {
        setEditData((d) => {
            if (!d) return d;
            const voci = [...d.voci];
            voci[idx] = { ...voci[idx], ...patch };
            return recomputeTotals({ ...d, voci });
        });
    };

    const addVoce = () => {
        setEditData((d) => {
            if (!d) return d;
            const voci = [...d.voci, { tipo: 'materiali', descrizione: '', quantita: 1, unita: 'cad', prezzo_unitario: 0, totale: 0 }];
            return recomputeTotals({ ...d, voci });
        });
    };

    const removeVoce = (idx) => {
        setEditData((d) => {
            if (!d) return d;
            const voci = d.voci.filter((_, i) => i !== idx);
            return recomputeTotals({ ...d, voci });
        });
    };

    const updateIva = (newIva) => {
        setEditData((d) => d ? recomputeTotals({ ...d, iva_percentuale: Number(newIva) || 0 }) : d);
    };

    const saveEdit = async () => {
        if (!openDetail || !editData) return;
        setSavingEdit(true);
        try {
            const finalContent = recomputeTotals(editData);
            const res = await api.put(`/preventivi/${openDetail.id}`, { contenuto: finalContent });
            const updated = res.data;
            setOpenDetail(updated);
            setItems((prev) => prev.map((p) => p.id === updated.id ? updated : p));
            toast.success(t('pages.prev_edit_saved'));
            setEditMode(false);
            setEditData(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('pages.prev_edit_save_err'));
        } finally {
            setSavingEdit(false);
        }
    };

    /**
     * Auto-save pending edits when the detail dialog is closing (X clicked, route changed via bottom nav, etc).
     * Fixes user complaint: clicking another tab while editing dismissed changes silently.
     */
    const handleDetailOpenChange = async (isOpen) => {
        if (isOpen) return; // never called with true here
        // closing — if we're mid-edit with unsaved changes, persist them silently first
        if (editMode && editData && openDetail) {
            try {
                const finalContent = recomputeTotals(editData);
                const res = await api.put(`/preventivi/${openDetail.id}`, { contenuto: finalContent });
                const updated = res.data;
                setItems((prev) => prev.map((p) => p.id === updated.id ? updated : p));
                toast.success(t('pages.prev_edit_saved'));
            } catch (e) {
                toast.error(e.response?.data?.detail || t('pages.prev_edit_save_err'));
            }
        }
        setEditMode(false);
        setEditData(null);
        setOpenDetail(null);
    };

    /**
     * Persist pending edit on tab/page change (best-effort, sync attempt via fetch+keepalive).
     * Triggered on component unmount (route change) or window beforeunload.
     */
    useEffect(() => {
        const flushPendingEdit = () => {
            if (!editMode || !editData || !openDetail) return;
            try {
                const finalContent = recomputeTotalsRef.current(editData);
                const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/preventivi/${openDetail.id}`;
                const token = localStorage.getItem('cantiere_token');
                fetch(url, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({ contenuto: finalContent }),
                    keepalive: true, // allows the request to outlive page unload
                });
            } catch (_) { /* best-effort */ }
        };
        window.addEventListener('beforeunload', flushPendingEdit);
        return () => {
            // on unmount (e.g. user clicks a bottom-nav tab and React unmounts this page)
            flushPendingEdit();
            window.removeEventListener('beforeunload', flushPendingEdit);
        };
    }, [editMode, editData, openDetail]);
    // -------- /Edit mode --------


    const copyLink = async () => {
        const tok = await ensureShareToken();
        if (!tok) return;
        try {
            await navigator.clipboard.writeText(publicUrl(tok));
            toast.success(t('pages.prev_share_copied'));
        } catch (e) {
            toast.error(t('pages.prev_toast_gen_err'));
        }
    };

    const shareWhatsApp = async () => {
        const tok = await ensureShareToken();
        if (!tok || !openDetail) return;
        if (!openDetail.cliente_telefono && !phoneEdit) {
            toast.message(t('pages.prev_no_phone'));
        }
        const msg = t('pages.prev_wa_message', {
            cliente: openDetail.cliente,
            numero: openDetail.numero,
            totale: openDetail.contenuto?.totale_finale?.toLocaleString(i18n.language) || '0',
        });
        const url = publicUrl(tok);
        const fullMsg = encodeURIComponent(`${msg}\n${url}`);
        const phone = normalizePhone(openDetail.cliente_telefono || phoneEdit);
        const waUrl = phone ? `https://wa.me/${phone}?text=${fullMsg}` : `https://wa.me/?text=${fullMsg}`;
        window.open(waUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <div data-testid="preventivi-page" className="pb-20 md:pb-0">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-display text-4xl md:text-5xl">{t('pages.prev_title')}</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('pages.prev_subtitle')}</p>
                </div>
                <Button
                    onClick={() => setOpenForm(true)}
                    className="hidden sm:flex bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold gap-2 h-11"
                    data-testid="preventivi-new-button"
                >
                    <Sparkles className="w-4 h-4" /> {t('pages.prev_new')}
                </Button>
            </div>

            <Card className="overflow-hidden">
                {items.length === 0 ? (
                    <div className="p-10 text-center text-[hsl(var(--muted-foreground))]">
                        <FileText className="w-12 h-12 mx-auto mb-3" />
                        <p className="mb-3">{t('pages.prev_empty')}</p>
                        <Button onClick={() => setOpenForm(true)} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]"><Plus className="w-4 h-4 mr-1" /> {t('dashboard.create_first')}</Button>
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <table className="w-full text-sm hidden md:table">
                            <thead className="bg-[hsl(var(--muted))]">
                                <tr className="text-left">
                                    <th className="p-3 font-semibold">{t('pages.prev_col_numero')}</th>
                                    <th className="p-3 font-semibold">{t('pages.prev_col_cliente')}</th>
                                    <th className="p-3 font-semibold">{t('pages.prev_col_categoria')}</th>
                                    <th className="p-3 font-semibold">{t('pages.fat_col_status')}</th>
                                    <th className="p-3 font-semibold text-right">{t('pages.prev_col_totale')}</th>
                                    <th className="p-3 font-semibold"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((p) => (
                                    <tr key={p.id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/30" data-testid={`preventivo-row-${p.id}`}>
                                        <td className="p-3 font-mono text-xs">{p.numero}</td>
                                        <td className="p-3 font-medium">{p.cliente}</td>
                                        <td className="p-3"><Badge variant="secondary">{p.categoria}</Badge></td>
                                        <td className="p-3">{wfBadge(p)}</td>
                                        <td className="p-3 text-right font-display text-lg tabular-nums">€{p.contenuto?.totale_finale?.toLocaleString('it-IT') || '0'}</td>
                                        <td className="p-3 text-right whitespace-nowrap">
                                            <Button size="sm" variant="ghost" onClick={() => setOpenDetail(p)} data-testid={`preventivo-view-${p.id}`}>{t('common.view')}</Button>
                                            <Button size="sm" variant="ghost" onClick={() => remove(p.id)} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {/* Mobile cards */}
                        <ul className="md:hidden divide-y divide-[hsl(var(--border))]">
                            {items.map((p) => (
                                <li key={p.id} className="p-4 active:bg-[hsl(var(--muted))]/40" data-testid={`preventivo-card-${p.id}`}>
                                    <button onClick={() => setOpenDetail(p)} className="w-full text-left" data-testid={`preventivo-card-open-${p.id}`}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{p.numero}</span>
                                            <span className="font-display text-lg tabular-nums">€{p.contenuto?.totale_finale?.toLocaleString('it-IT') || '0'}</span>
                                        </div>
                                        <div className="font-semibold truncate">{p.cliente}</div>
                                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                                            <Badge variant="secondary" className="text-xs">{p.categoria}</Badge>
                                            {wfBadge(p)}
                                            {p.cliente_telefono && <Phone className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />}
                                        </div>
                                    </button>
                                    <div className="mt-2 flex justify-end">
                                        <Button size="sm" variant="ghost" onClick={() => remove(p.id)} className="text-[hsl(var(--destructive))] h-8">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </Card>

            {/* Floating Action Button (mobile only) */}
            <Button
                onClick={() => setOpenForm(true)}
                className="sm:hidden fixed bottom-20 right-4 z-40 h-14 w-14 rounded-full shadow-lg bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] p-0"
                aria-label={t('pages.prev_new')}
                data-testid="preventivi-fab"
            >
                <Sparkles className="w-6 h-6" />
            </Button>

            {/* New preventivo dialog - fullscreen on mobile */}
            <Dialog open={openForm} onOpenChange={handleOpenFormChange}>
                <DialogContent className="max-w-2xl sm:max-h-[90vh] sm:rounded-lg max-h-screen h-screen sm:h-auto overflow-y-auto p-4 pb-28 sm:p-6 sm:pb-6 rounded-none sm:rounded-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">{t('pages.prev_form_title')}</DialogTitle>
                    </DialogHeader>

                    {/* Draft recovery banner */}
                    {draftHint && (
                        <div
                            className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-lg border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10"
                            data-testid="preventivo-draft-banner"
                        >
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                                <FileText className="w-4 h-4 text-[hsl(var(--accent))] mt-0.5 flex-shrink-0" />
                                <div className="text-xs sm:text-sm leading-snug">
                                    <div className="font-semibold">{t('pages.draft_banner_title')}</div>
                                    <div className="text-[hsl(var(--muted-foreground))]">
                                        {t('pages.draft_banner_hint', { when: formatSavedAt(draftHint.savedAt, i18n.language) })}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="text-xs h-8 border-[hsl(0_60%_60%)]/50 text-[hsl(0_70%_45%)] hover:bg-[hsl(0_70%_96%)]"
                                    onClick={discardDraft}
                                    data-testid="preventivo-draft-discard"
                                >
                                    <Trash2 className="w-3 h-3 mr-1" /> {t('pages.draft_discard')}
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    className="text-xs h-8 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] hover:bg-[hsl(45_100%_48%)] font-bold"
                                    onClick={resumeDraft}
                                    data-testid="preventivo-draft-resume"
                                >
                                    <Wand2 className="w-3 h-3 mr-1" /> {t('pages.draft_resume')}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Quick mode toggle */}
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-[hsl(var(--border))] p-3 mt-3 bg-[hsl(var(--muted))]/30">
                        <div className="flex-1">
                            <div className="text-sm font-semibold flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />
                                {quickMode ? t('pages.prev_quick_mode_label') : t('pages.prev_full_mode_label')}
                            </div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.prev_quick_mode_hint')}</div>
                        </div>
                        <Switch checked={quickMode} onCheckedChange={setQuickMode} data-testid="preventivi-quick-toggle" />
                    </div>

                    {/* 1-click reuse last */}
                    <div className="mt-2">
                        <Button type="button" size="sm" variant="outline" onClick={reuseLast} className="gap-1 text-xs h-8" data-testid="preventivi-reuse-last">
                            <Wand2 className="w-3 h-3" /> {t('pages.prev_last_used')}
                        </Button>
                    </div>

                    <form onSubmit={generate} className="space-y-4 mt-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label>{t('pages.prev_form_cliente')}</Label>
                                <Input required value={form.cliente} onChange={(e) => setForm({...form, cliente: e.target.value})} className="mt-1 h-11" data-testid="preventivo-cliente" />
                            </div>
                            <div>
                                <Label className="flex items-center gap-1"><Phone className="w-3 h-3" /> {t('pages.prev_form_phone')}</Label>
                                <Input type="tel" inputMode="tel" placeholder="+39 333 1234567" value={form.cliente_telefono} onChange={(e) => setForm({...form, cliente_telefono: e.target.value})} className="mt-1 h-11" data-testid="preventivo-telefono" />
                            </div>
                            <div>
                                <Label>{t('pages.prev_form_categoria')}</Label>
                                <Select value={form.categoria} onValueChange={(v) => setForm({...form, categoria: v})}>
                                    <SelectTrigger className="mt-1 h-11" data-testid="preventivo-categoria"><SelectValue /></SelectTrigger>
                                    <SelectContent>{CATEGORIE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            {!quickMode && (
                                <>
                                    <div>
                                        <Label>{t('pages.prev_form_indirizzo')}</Label>
                                        <Input value={form.indirizzo} onChange={(e) => setForm({...form, indirizzo: e.target.value})} className="mt-1 h-11" />
                                    </div>
                                    <div>
                                        <Label>{t('pages.prev_form_metratura')}</Label>
                                        <Input type="number" inputMode="decimal" step="0.01" value={form.metratura} onChange={(e) => setForm({...form, metratura: e.target.value})} className="mt-1 h-11" />
                                    </div>
                                    <div>
                                        <Label>{t('pages.prev_form_iva')}</Label>
                                        <Select value={String(form.iva)} onValueChange={(v) => setForm({...form, iva: parseInt(v)})}>
                                            <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="4">{t('pages.prev_form_iva_4')}</SelectItem>
                                                <SelectItem value="10">{t('pages.prev_form_iva_10')}</SelectItem>
                                                <SelectItem value="22">{t('pages.prev_form_iva_22')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </>
                            )}
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <Label>{t('pages.prev_form_descr')}</Label>
                                <Button type="button" size="sm" variant="ghost" onClick={() => setAiDescDialog(true)} className="gap-1 h-7 text-xs text-[hsl(var(--accent))]" data-testid="preventivo-ai-desc-button">
                                    <Wand2 className="w-3 h-3" /> {t('pages.prev_form_descr_ai')}
                                </Button>
                            </div>
                            <Textarea required rows={4} value={form.descrizione_lavoro} onChange={(e) => setForm({...form, descrizione_lavoro: e.target.value})} placeholder={t('pages.prev_form_descr_ph')} className="mt-1" data-testid="preventivo-descrizione" />
                            {!quickMode && (
                                <div className="mt-2 flex">
                                    <Button type="button" size="sm" variant="outline" onClick={() => setSuggDialog(true)} disabled={!form.descrizione_lavoro || !form.categoria} className="gap-1 text-xs" data-testid="preventivo-suggerimenti-button">
                                        <Package className="w-3 h-3" /> {t('pages.prev_form_sugg_ai')}
                                    </Button>
                                </div>
                            )}
                        </div>
                        {!quickMode && (
                            <div>
                                <Label>{t('pages.prev_form_note')}</Label>
                                <Textarea rows={2} value={form.note_aggiuntive} onChange={(e) => setForm({...form, note_aggiuntive: e.target.value})} className="mt-1" />
                            </div>
                        )}
                        <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-12" data-testid="preventivo-generate-button">
                            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('pages.prev_form_generating')}</> : <><Sparkles className="w-4 h-4 mr-2" /> {quickMode ? t('pages.prev_quick_submit') : t('common.generate_ai')}</>}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Detail dialog - fullscreen on mobile */}
            <Dialog open={!!openDetail} onOpenChange={(o) => { if (!o) handleDetailOpenChange(false); }}>
                <DialogContent className="max-w-3xl sm:max-h-[90vh] max-h-screen h-screen sm:h-auto overflow-y-auto p-4 pb-32 sm:p-6 sm:pb-6 rounded-none sm:rounded-lg">
                    {openDetail && (
                        <div data-testid="preventivo-detail">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">{t('pages.prev_detail_title')} {openDetail.numero}</DialogTitle>
                            </DialogHeader>
                            <div className="flex flex-wrap justify-between items-start gap-2 mb-4 mt-2 pb-4 border-b">
                                <div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.prev_col_cliente')}</div>
                                    <div className="font-semibold">{openDetail.cliente}</div>
                                    {openDetail.indirizzo && <div className="text-sm text-[hsl(var(--muted-foreground))]">{openDetail.indirizzo}</div>}
                                </div>
                                <div className="flex flex-wrap gap-2 no-print">
                                    {!editMode ? (
                                        <>
                                            <Button onClick={startEdit} variant="outline" size="sm" className="gap-2" data-testid="preventivo-edit-button"><Pencil className="w-4 h-4" /> {t('pages.prev_edit_button')}</Button>
                                            <Button onClick={() => downloadDocPdf('preventivi', openDetail.id, `Preventivo_${(openDetail.numero || 'doc').replace('/', '_')}.pdf`)} size="sm" className="gap-2 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold hover:bg-[hsl(45_100%_48%)]" data-testid="preventivo-pdf"><FileText className="w-4 h-4" /> {t('pages.prev_download_pdf') || 'Scarica PDF'}</Button>
                                            <Button onClick={() => window.print()} variant="outline" size="sm" className="gap-2" data-testid="preventivo-print"><Printer className="w-4 h-4" /> {t('common.print')}</Button>
                                            <Button onClick={() => setEmailDialog(openDetail)} size="sm" className="gap-2 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]" data-testid="preventivo-email-button"><Mail className="w-4 h-4" /> {t('common.email')}</Button>
                                            <Button onClick={generaFattura} disabled={generatingFattura} size="sm" variant="outline" className="gap-2 border-[hsl(var(--accent))] text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10" data-testid="preventivo-genera-fattura">
                                                {generatingFattura ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />} {t('pages.prev_genera_fattura')}
                                            </Button>
                                            <Button onClick={() => setFollowUp(openDetail)} size="sm" variant="outline" className="gap-2" data-testid="preventivo-followup-button"><Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" /> {t('pages.prev_followup_ai')}</Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button onClick={saveEdit} disabled={savingEdit} size="sm" className="gap-2 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold" data-testid="preventivo-save-edit">
                                                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('pages.prev_edit_save')}
                                            </Button>
                                            <Button onClick={cancelEdit} variant="outline" size="sm" className="gap-2" data-testid="preventivo-cancel-edit"><X className="w-4 h-4" /> {t('pages.prev_edit_cancel')}</Button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* WORKFLOW Accetta + Proforma */}
                            {(() => {
                                const wf = openDetail.stato_workflow || openDetail.stato || 'bozza';
                                const isFinal = ['accettato', 'lavori_in_corso', 'completato'].includes(wf);
                                const wfLabels = {
                                    bozza: t('pages.prev_workflow_bozza'),
                                    inviata: t('pages.prev_workflow_inviato'),
                                    inviato: t('pages.prev_workflow_inviato'),
                                    accettato: t('pages.prev_workflow_accettato'),
                                    lavori_in_corso: t('pages.prev_workflow_in_corso'),
                                    completato: t('pages.prev_workflow_completato'),
                                    rifiutato: t('pages.prev_workflow_rifiutato'),
                                };
                                const wfColors = {
                                    bozza: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]',
                                    inviata: 'bg-[hsl(210_90%_94%)] text-[hsl(210_90%_40%)]',
                                    inviato: 'bg-[hsl(210_90%_94%)] text-[hsl(210_90%_40%)]',
                                    accettato: 'bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)]',
                                    lavori_in_corso: 'bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)]',
                                    completato: 'bg-[hsl(145_55%_34%)] text-white',
                                    rifiutato: 'bg-[hsl(0_80%_95%)] text-[hsl(0_72%_52%)]',
                                };
                                return (
                                    <div className="mb-4 rounded-lg border border-[hsl(var(--accent))]/30 p-4 bg-[hsl(var(--accent))]/5 no-print" data-testid="preventivo-workflow-card">
                                        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                                            <div className="flex items-center gap-2">
                                                <Badge className={wfColors[wf] || wfColors.bozza}>{wfLabels[wf] || wf}</Badge>
                                                {proforma && (() => {
                                                    let badgeClass = 'bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)]';
                                                    let statoLabel = t('pages.prev_proforma_stato_emessa');
                                                    if (proforma.stato === 'pagata') {
                                                        badgeClass = 'bg-[hsl(145_55%_34%)] text-white';
                                                        statoLabel = t('pages.prev_proforma_stato_pagata');
                                                    } else if (proforma.stato === 'scaduta') {
                                                        // Use prominent OverdueBadge for scaduta
                                                        return (
                                                            <div className="inline-flex items-center gap-2" data-testid="proforma-badge">
                                                                <Badge className="bg-[hsl(220_13%_10%)] text-white text-xs">
                                                                    {t('pages.prev_proforma_label')} {proforma.numero}
                                                                </Badge>
                                                                <OverdueBadge dataScadenza={proforma.data_scadenza} size="md" />
                                                            </div>
                                                        );
                                                    }
                                                    return (
                                                        <Badge className={badgeClass} data-testid="proforma-badge">
                                                            {t('pages.prev_proforma_label')} {proforma.numero} · {statoLabel}
                                                        </Badge>
                                                    );
                                                })()}
                                            </div>
                                            {/* Action buttons */}
                                            <div className="flex flex-wrap gap-2">
                                                {!isFinal && !proforma && (
                                                    <Button onClick={() => { setAccettaPct(20); setAccettaNote(''); setAccettaDialog(true); }} size="sm" className="gap-2 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold hover:bg-[hsl(45_100%_48%)]" data-testid="preventivo-accetta-button">
                                                        <CheckCircle2 className="w-4 h-4" /> {t('pages.prev_accetta_button')}
                                                    </Button>
                                                )}
                                                {proforma && proforma.stato !== 'pagata' && (
                                                    <Button onClick={() => setSendDialog(true)} size="sm" className="gap-2 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold hover:bg-[hsl(45_100%_48%)]" data-testid="proforma-send-client-button">
                                                        <Send className="w-4 h-4" /> {t('pages.prev_pt_send_to_client')}
                                                    </Button>
                                                )}
                                                {proforma && proforma.stato !== 'pagata' && (
                                                    <Button onClick={() => { setPagRiferimento(''); setPagMetodo('bonifico'); setConfermaPagDialog(true); }} size="sm" className="gap-2 bg-[hsl(145_55%_34%)] text-white hover:bg-[hsl(145_55%_28%)]" data-testid="proforma-conferma-button">
                                                        <CreditCard className="w-4 h-4" /> {t('pages.prev_proforma_conferma_btn')}
                                                    </Button>
                                                )}
                                                {proforma && (
                                                    <Button onClick={() => downloadDocPdf('proforme', proforma.id, `Proforma_${(proforma.numero || 'doc').replace('/', '_')}.pdf`)} size="sm" variant="outline" className="gap-2" data-testid="proforma-pdf-button">
                                                        <FileText className="w-4 h-4" /> {t('pages.prev_download_pdf_proforma') || 'Scarica PDF proforma'}
                                                    </Button>
                                                )}
                                                {proforma && (
                                                    <Button onClick={copyClientLink} size="sm" variant="outline" className="gap-2" data-testid="proforma-copy-link-button">
                                                        <Copy className="w-4 h-4" /> {t('pages.prev_pt_copy_link')}
                                                    </Button>
                                                )}
                                                {proforma && (
                                                    <Button onClick={() => window.open(`/p/proforma/${proforma.share_token}`, '_blank')} size="sm" variant="outline" className="gap-2" data-testid="proforma-view-button">
                                                        <Eye className="w-4 h-4" /> {t('pages.prev_pt_preview_client')}
                                                    </Button>
                                                )}
                                                {proforma && proforma.stato === 'pagata' && openDetail.cantiere_id && (
                                                    <Button onClick={() => navigate('/app/sal')} size="sm" variant="outline" className="gap-2 border-[hsl(145_55%_34%)] text-[hsl(145_55%_34%)]" data-testid="preventivo-vai-sal">
                                                        <Building2 className="w-4 h-4" /> SAL
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        {proforma && (
                                            <>
                                                <div className="grid sm:grid-cols-3 gap-3 mt-3 pt-3 border-t border-[hsl(var(--border))]">
                                                    <div>
                                                        <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.prev_proforma_totale_lavoro')}</div>
                                                        <div className="font-display text-lg tabular-nums">€{Number(proforma.totale_preventivo).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.prev_proforma_importo')} ({proforma.anticipo_pct}%)</div>
                                                        <div className="font-display text-lg tabular-nums text-[hsl(var(--accent))]" data-testid="proforma-importo">€{Number(proforma.importo).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                                                    </div>
                                                    {proforma.iban && (
                                                        <div>
                                                            <div className="text-xs text-[hsl(var(--muted-foreground))]">IBAN</div>
                                                            <div className="font-mono text-xs tracking-wider truncate" title={proforma.iban}>{proforma.iban}</div>
                                                            {proforma.banca_nome && <div className="text-xs text-[hsl(var(--muted-foreground))]">{proforma.banca_nome}</div>}
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Payment terms + scadenza */}
                                                {proforma.stato !== 'pagata' && (
                                                    <div className="grid sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-[hsl(var(--border))]">
                                                        <div>
                                                            <Label className="text-xs flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {t('pages.prev_pt_label')}</Label>
                                                            <Select value={proforma.payment_terms || '30gg_fine_mese'} onValueChange={updatePaymentTerms}>
                                                                <SelectTrigger className="mt-1 h-10" data-testid="proforma-payment-terms-select">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {PAYMENT_TERM_OPTIONS.map((o) => (
                                                                        <SelectItem key={o.value} value={o.value}>{t(o.labelKey)}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs flex items-center gap-1">
                                                                {proforma.stato === 'scaduta'
                                                                    ? <AlertCircle className="w-3 h-3 text-[hsl(0_75%_45%)]" />
                                                                    : <CalendarClock className="w-3 h-3" />
                                                                } {t('pages.prev_pt_due_date')}
                                                            </Label>
                                                            <div className={`mt-1 h-10 flex items-center font-display text-base tabular-nums ${proforma.stato === 'scaduta' ? 'text-[hsl(0_75%_45%)]' : ''}`} data-testid="proforma-due-date">
                                                                {formatDate(proforma.data_scadenza)}
                                                                {proforma.stato === 'scaduta' && (
                                                                    <span className="ml-2 text-xs font-semibold">({t('pages.prev_proforma_stato_scaduta')})</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* WhatsApp share box */}
                            <div className="mb-4 rounded-lg border border-[hsl(var(--border))] p-3 bg-[hsl(var(--muted))]/30 no-print">
                                <div className="flex items-center gap-2 mb-2">
                                    <Share2 className="w-4 h-4 text-[hsl(var(--accent))]" />
                                    <span className="font-semibold text-sm">{t('pages.prev_share_wa')}</span>
                                </div>
                                <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                                    <div>
                                        <Label className="text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> {t('pages.prev_form_phone')}</Label>
                                        <Input
                                            type="tel"
                                            inputMode="tel"
                                            placeholder="+39 333 1234567"
                                            value={phoneEdit}
                                            onChange={(e) => setPhoneEdit(e.target.value)}
                                            onBlur={() => { if (phoneEdit !== (openDetail.cliente_telefono || '')) savePhone(); }}
                                            className="mt-1 h-10"
                                            data-testid="preventivo-detail-telefono"
                                        />
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button onClick={shareWhatsApp} size="sm" className="gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white h-10" data-testid="preventivo-wa-share">
                                            <MessageCircle className="w-4 h-4" /> WhatsApp
                                        </Button>
                                        <Button onClick={copyLink} size="sm" variant="outline" className="gap-2 h-10" data-testid="preventivo-copy-link">
                                            <LinkIcon className="w-4 h-4" /> {t('pages.prev_share_link')}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* TITOLO + DESCRIZIONE */}
                            {!editMode ? (
                                <>
                                    <h3 className="font-display text-xl mb-1">{openDetail.contenuto?.titolo}</h3>
                                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{openDetail.contenuto?.descrizione_lavoro}</p>
                                </>
                            ) : (
                                <div className="mb-4 space-y-3 p-3 rounded-md border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5">
                                    <div>
                                        <Label className="text-xs">{t('pages.prev_edit_title')}</Label>
                                        <Input value={editData?.titolo || ''} onChange={(e) => setEditData((d) => ({ ...d, titolo: e.target.value }))} className="mt-1 h-10" data-testid="edit-titolo" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">{t('pages.prev_edit_descr')}</Label>
                                        <Textarea rows={3} value={editData?.descrizione_lavoro || ''} onChange={(e) => setEditData((d) => ({ ...d, descrizione_lavoro: e.target.value }))} className="mt-1" data-testid="edit-descrizione" />
                                    </div>
                                </div>
                            )}

                            {/* VOCI: read-only table + cards, OR editable list */}
                            {!editMode ? (
                                <>
                                    {/* Desktop: table; Mobile: cards */}
                                    <div className="hidden md:block overflow-x-auto">
                                        <table className="w-full text-sm mb-4">
                                            <thead className="bg-[hsl(var(--muted))]"><tr><th className="p-2 text-left">{t('pages.prev_col_tipo')}</th><th className="p-2 text-left">{t('pages.prev_col_descr')}</th><th className="p-2 text-right">{t('pages.prev_col_qta')}</th><th className="p-2 text-right">{t('pages.prev_col_prezzo')}</th><th className="p-2 text-right">{t('pages.prev_col_totale')}</th></tr></thead>
                                            <tbody>
                                                {(openDetail.contenuto?.voci || []).map((v, i) => (
                                                    <tr key={i} className="border-t"><td className="p-2"><Badge variant="outline" className="text-xs">{v.tipo}</Badge></td><td className="p-2">{v.descrizione}</td><td className="p-2 text-right tabular-nums">{v.quantita} {v.unita}</td><td className="p-2 text-right tabular-nums">€{v.prezzo_unitario}</td><td className="p-2 text-right font-semibold tabular-nums">€{v.totale}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="md:hidden space-y-2 mb-4">
                                        {(openDetail.contenuto?.voci || []).map((v, i) => (
                                            <div key={i} className="p-3 rounded-md border border-[hsl(var(--border))]">
                                                <div className="flex items-center justify-between mb-1">
                                                    <Badge variant="outline" className="text-xs">{v.tipo}</Badge>
                                                    <span className="font-semibold tabular-nums">€{v.totale}</span>
                                                </div>
                                                <div className="text-sm">{v.descrizione}</div>
                                                <div className="text-xs text-[hsl(var(--muted-foreground))] tabular-nums">{v.quantita} {v.unita} × €{v.prezzo_unitario}</div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="space-y-3 mb-4">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-sm font-semibold">{t('pages.prev_edit_voci')}</Label>
                                        <Button onClick={addVoce} size="sm" variant="outline" className="gap-1 h-8" data-testid="edit-add-voce"><Plus className="w-3 h-3" /> {t('pages.prev_edit_add_voce')}</Button>
                                    </div>
                                    {(editData?.voci || []).map((v, i) => (
                                        <div key={i} className="p-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] space-y-2" data-testid={`edit-voce-${i}`}>
                                            <div className="grid grid-cols-12 gap-2">
                                                <div className="col-span-12 sm:col-span-3">
                                                    <Label className="text-xs">{t('pages.prev_col_tipo')}</Label>
                                                    <Select value={v.tipo} onValueChange={(val) => updateVoce(i, { tipo: val })}>
                                                        <SelectTrigger className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="materiali">materiali</SelectItem>
                                                            <SelectItem value="manodopera">manodopera</SelectItem>
                                                            <SelectItem value="trasporto">trasporto</SelectItem>
                                                            <SelectItem value="smaltimento">smaltimento</SelectItem>
                                                            <SelectItem value="altro">altro</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="col-span-12 sm:col-span-9">
                                                    <Label className="text-xs">{t('pages.prev_col_descr')}</Label>
                                                    <Textarea rows={2} value={v.descrizione} onChange={(e) => updateVoce(i, { descrizione: e.target.value })} className="mt-1 text-sm" />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-12 gap-2 items-end">
                                                <div className="col-span-4 sm:col-span-3">
                                                    <Label className="text-xs">{t('pages.prev_col_qta')}</Label>
                                                    <Input type="number" inputMode="decimal" step="0.01" value={v.quantita} onChange={(e) => updateVoce(i, { quantita: e.target.value })} className="mt-1 h-9" />
                                                </div>
                                                <div className="col-span-4 sm:col-span-2">
                                                    <Label className="text-xs">{t('pages.prev_edit_unita')}</Label>
                                                    <Input value={v.unita} onChange={(e) => updateVoce(i, { unita: e.target.value })} placeholder="mq" className="mt-1 h-9" />
                                                </div>
                                                <div className="col-span-4 sm:col-span-3">
                                                    <Label className="text-xs">{t('pages.prev_col_prezzo')} €</Label>
                                                    <Input type="number" inputMode="decimal" step="0.01" value={v.prezzo_unitario} onChange={(e) => updateVoce(i, { prezzo_unitario: e.target.value })} className="mt-1 h-9" />
                                                </div>
                                                <div className="col-span-8 sm:col-span-2 text-right">
                                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('pages.prev_col_totale')}</div>
                                                    <div className="font-semibold tabular-nums">€{v.totale}</div>
                                                </div>
                                                <div className="col-span-4 sm:col-span-2 flex justify-end">
                                                    <Button onClick={() => removeVoce(i)} size="sm" variant="ghost" className="text-[hsl(var(--destructive))] gap-1 h-9" data-testid={`edit-remove-voce-${i}`}>
                                                        <Trash2 className="w-3 h-3" /> {t('pages.prev_edit_remove_voce')}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* TOTALI */}
                            <div className="flex justify-end">
                                <div className="w-full sm:w-72 space-y-1 text-sm">
                                    <div className="flex justify-between"><span>{t('pages.prev_detail_subtotale')}</span><span className="tabular-nums">€{editMode ? editData?.subtotale : openDetail.contenuto?.subtotale}</span></div>
                                    {!editMode ? (
                                        <div className="flex justify-between"><span>{t('pages.prev_form_iva')} {openDetail.contenuto?.iva_percentuale}%</span><span className="tabular-nums">€{openDetail.contenuto?.iva_importo}</span></div>
                                    ) : (
                                        <div className="flex justify-between items-center gap-2">
                                            <span className="flex items-center gap-1">{t('pages.prev_form_iva')}
                                                <Select value={String(editData?.iva_percentuale || 22)} onValueChange={updateIva}>
                                                    <SelectTrigger className="h-8 w-20 text-xs"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="0">0%</SelectItem>
                                                        <SelectItem value="4">4%</SelectItem>
                                                        <SelectItem value="10">10%</SelectItem>
                                                        <SelectItem value="22">22%</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </span>
                                            <span className="tabular-nums">€{editData?.iva_importo}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between font-display text-2xl border-t pt-2"><span>{t('pages.prev_detail_total')}</span><span className="tabular-nums" data-testid="preventivo-total">€{editMode ? editData?.totale_finale : openDetail.contenuto?.totale_finale}</span></div>
                                </div>
                            </div>
                            {!editMode ? (
                                <>
                                    <div className="mt-4 text-sm text-[hsl(var(--muted-foreground))]" dangerouslySetInnerHTML={sanitizeStrict(t('pages.prev_detail_tempi', { days: openDetail.contenuto?.tempi_stimati_giorni }))} />
                                    {openDetail.contenuto?.note && <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">{openDetail.contenuto.note}</p>}
                                </>
                            ) : (
                                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">{t('pages.prev_edit_tempi')}</Label>
                                        <Input type="number" inputMode="numeric" value={editData?.tempi_stimati_giorni || 0} onChange={(e) => setEditData((d) => ({ ...d, tempi_stimati_giorni: Number(e.target.value) || 0 }))} className="mt-1 h-10" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Label className="text-xs">{t('pages.prev_edit_note')}</Label>
                                        <Textarea rows={2} value={editData?.note || ''} onChange={(e) => setEditData((d) => ({ ...d, note: e.target.value }))} className="mt-1" />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <SendEmailDialog
                open={!!emailDialog}
                onOpenChange={(o) => !o && setEmailDialog(null)}
                endpoint={emailDialog ? `/email/preventivo/${emailDialog.id}` : ''}
                docLabel="preventivo"
                onSent={() => load()}
            />

            {/* Send proforma to client (with public payment link) */}
            <SendEmailDialog
                open={sendDialog}
                onOpenChange={setSendDialog}
                endpoint={proforma ? `/email/proforma/${proforma.id}` : ''}
                docLabel="proforma"
                defaultEmail={proforma?.cliente_email || openDetail?.cliente_email || ''}
                onSent={() => { setSendDialog(false); }}
            />

            <DescrizioneAIDialog
                open={aiDescDialog}
                onOpenChange={setAiDescDialog}
                onApply={(text, titolo) => setForm({...form, descrizione_lavoro: text})}
            />

            <SuggerimentiDialog
                open={suggDialog}
                onOpenChange={setSuggDialog}
                categoria={form.categoria}
                descrizione={form.descrizione_lavoro}
                metratura={form.metratura ? parseFloat(form.metratura) : null}
            />

            <FollowUpDialog
                open={!!followUp}
                onOpenChange={(o) => !o && setFollowUp(null)}
                tipo="preventivo"
                riferimentoId={followUp?.id}
                riferimentoLabel={followUp ? `${followUp.numero} · ${followUp.cliente}` : ''}
            />

            {/* Accept quote + create proforma */}
            <AlertDialog open={accettaDialog} onOpenChange={setAccettaDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.prev_accetta_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('pages.prev_accetta_desc')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-3 my-3">
                        <div>
                            <Label className="text-xs">{t('pages.prev_accetta_anticipo_label')}</Label>
                            <div className="relative mt-1">
                                <Input type="number" inputMode="decimal" min="0" max="100" value={accettaPct} onChange={(e) => setAccettaPct(e.target.value)} className="h-11 pr-8" data-testid="accetta-pct-input" />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[hsl(var(--muted-foreground))]">%</span>
                            </div>
                            {openDetail && (
                                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                                    = €{Number((openDetail.contenuto?.totale_finale || 0) * (parseFloat(accettaPct) || 0) / 100).toLocaleString('it-IT', { minimumFractionDigits: 2 })} {t('pages.prev_proforma_importo').toLowerCase()}
                                </div>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs">{t('pages.prev_accetta_note')}</Label>
                            <Textarea rows={2} value={accettaNote} onChange={(e) => setAccettaNote(e.target.value)} className="mt-1" placeholder="" />
                        </div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-start gap-2 p-2 rounded bg-[hsl(var(--muted))]/40">
                            <CreditCard className="w-4 h-4 mt-0.5 text-[hsl(var(--accent))]" />
                            <span>{t('pages.prev_proforma_iban_cliente_info')}</span>
                        </div>
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={accettaLoading}>{t('common.cancel') || 'Annulla'}</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); accettaPreventivo(); }} disabled={accettaLoading} className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] hover:bg-[hsl(45_100%_48%)]" data-testid="accetta-confirm-button">
                            {accettaLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                            {t('pages.prev_accetta_confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Confirm proforma payment received */}
            <AlertDialog open={confermaPaganDialog} onOpenChange={setConfermaPagDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-[hsl(145_55%_34%)]" /> {t('pages.prev_proforma_conferma_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('pages.prev_proforma_conferma_desc')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-3 my-3">
                        {proforma && (
                            <div className="rounded-lg bg-[hsl(var(--muted))]/40 p-3 text-sm">
                                <div className="flex justify-between"><span className="text-[hsl(var(--muted-foreground))]">{t('pages.prev_proforma_importo')}</span><span className="font-display text-lg tabular-nums">€{Number(proforma.importo).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span></div>
                            </div>
                        )}
                        <div>
                            <Label className="text-xs">{t('pages.prev_proforma_metodo')}</Label>
                            <Select value={pagMetodo} onValueChange={setPagMetodo}>
                                <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="bonifico">Bonifico</SelectItem>
                                    <SelectItem value="contanti">Contanti</SelectItem>
                                    <SelectItem value="assegno">Assegno</SelectItem>
                                    <SelectItem value="carta">Carta</SelectItem>
                                    <SelectItem value="altro">Altro</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">{t('pages.prev_proforma_riferimento')}</Label>
                            <Input value={pagRiferimento} onChange={(e) => setPagRiferimento(e.target.value)} className="mt-1 h-11" placeholder="CRO 123456789" data-testid="proforma-riferimento-input" />
                        </div>
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={pagLoading}>{t('common.cancel') || 'Annulla'}</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); confermaPagamentoProforma(); }} disabled={pagLoading} className="bg-[hsl(145_55%_34%)] hover:bg-[hsl(145_55%_28%)] text-white" data-testid="proforma-conferma-confirm">
                            {pagLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                            {t('pages.prev_proforma_conferma_btn')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
